// src/store/index.ts
import { configureStore, combineReducers } from '@reduxjs/toolkit'
import themeReducer from './themeSlice'
import authReducer from './slices/authSlice'
import datasetReducer from './slices/datasetSlice'
import modelReducer from './slices/modelSlice'
import annotationReducer from './slices/annotationSlice'
import predictionReducer from './slices/predictionSlice'
import projectReducer from './slices/projectSlice'
import uiSettingsReducer from './slices/uiSettingsSlice'
import { RESET_STATE } from './actions/resetState'
import { restoreAuth, logout } from './slices/authSlice'
import { setSelectedProjectId } from './slices/projectSlice'

import { tagApi } from '../services/tagApi'
import { contentApi } from '../services/contentApi'
import { projectsApi } from '../services/projectsApi'
import { datasetApi } from '../services/datasetApi'
import { modelsApi } from '../services/modelsApi'
import { annotationsApi } from '../services/annotationApi'
import { authApi } from '../services/authApi'
import { exportApi } from '../services/exportApi'
import { predictionApi } from '../services/predictionApi'
import { experimentalApi } from '../services/experimentalApi'
import { userApi } from '../services/userApi'
import { ollamaApi } from '../services/ollamaApi'
import { chatApi } from '../services/chatApi'
import { orgsApi, publicOrgsApi } from '../services/orgsApi'
import { setupListeners } from '@reduxjs/toolkit/query'

const combinedReducer = combineReducers({
  [tagApi.reducerPath]: tagApi.reducer,
  [contentApi.reducerPath]: contentApi.reducer,
  [projectsApi.reducerPath]: projectsApi.reducer,
  [datasetApi.reducerPath]: datasetApi.reducer,
  [modelsApi.reducerPath]: modelsApi.reducer,
  [annotationsApi.reducerPath]: annotationsApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [exportApi.reducerPath]: exportApi.reducer,
  [predictionApi.reducerPath]: predictionApi.reducer,
  [experimentalApi.reducerPath]: experimentalApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [ollamaApi.reducerPath]: ollamaApi.reducer,
  [chatApi.reducerPath]: chatApi.reducer,
  [orgsApi.reducerPath]: orgsApi.reducer,
  [publicOrgsApi.reducerPath]: publicOrgsApi.reducer,
  theme: themeReducer,
  auth: authReducer,
  dataset: datasetReducer,
  model: modelReducer,
  annotation: annotationReducer,
  prediction: predictionReducer,
  project: projectReducer,
  uiSettings: uiSettingsReducer,
})

const rootReducer = (state: any, action: any) => {
  if (action.type === RESET_STATE) {
    state = undefined
  }
  return combinedReducer(state, action)
}

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore non-serializable values in specific paths
        ignoredPaths: ['orgsApi.queries', 'publicOrgsApi.queries'],
        ignoredActions: ['orgsApi/executeQuery/fulfilled', 'publicOrgsApi/executeQuery/fulfilled'],
      },
    }).concat(
      tagApi.middleware,
      contentApi.middleware,
      projectsApi.middleware,
      datasetApi.middleware,
      modelsApi.middleware,
      annotationsApi.middleware,
      exportApi.middleware,
      predictionApi.middleware,
      authApi.middleware,
      experimentalApi.middleware,
      userApi.middleware,
      ollamaApi.middleware,
      chatApi.middleware,
      orgsApi.middleware,
      publicOrgsApi.middleware
    ),
})

// Enable refetching on window focus
setupListeners(store.dispatch)

// Restore auth and project state from localStorage
const accessToken = localStorage.getItem('access_token')
const refreshToken = localStorage.getItem('refresh_token')
const selectedProjectId = localStorage.getItem('selectedProjectId')

// Only restore auth if we have both tokens
if (accessToken && refreshToken) {
  store.dispatch(restoreAuth())

  // Only restore project if auth was successful and we have a project ID
  const state = store.getState()
  if (state.auth.isAuthenticated && selectedProjectId) {
    store.dispatch(setSelectedProjectId(selectedProjectId))
    // Trigger an immediate fetch of the project data
    store.dispatch(projectsApi.endpoints.getProject.initiate(selectedProjectId))
  }
} else {
  // If we don't have both tokens, clear everything
  store.dispatch(logout())
}

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
